/**
 * SearchPage — top-level palette surface, shadcn-canonical shape.
 *
 *   <CommandList>
 *     <CommandEmpty/>
 *     <CommandGroup heading="<Feature>">  ← one per feature
 *       <ResultRow .../>
 *       <ResultRow .../>
 *     </CommandGroup>
 *     <CommandSeparator/>
 *     <CommandGroup heading="<Feature>">
 *       …
 *     </CommandGroup>
 *   </CommandList>
 *
 * The grouping axis is FEATURE — "Mental Math" / "Notes" / "Chat" — not
 * type-of-action. Each row carries its own type signal: an inline crumb
 * chain for places, a shortcut hint for actions.
 *
 * No intermediate divs, no custom headers, no nested children inside
 * groups beyond CommandItem. cmdk's keyboard-nav + scrollIntoView work
 * the way they do everywhere else in shadcn.
 */
"use client";

import { Fragment, useState } from "react";
import { Command as CommandPrimitive } from "cmdk";
import { CircleNotch, MagnifyingGlass } from "@phosphor-icons/react";
import {
  CommandEmpty,
  CommandGroup,
  CommandList,
  CommandSeparator,
} from "@/shared/ui/command";
import { useResolver } from "../resolver";
import { ResultRow } from "../components/ResultRow";
import { PaletteFooter } from "../components/PaletteFooter";
import type { PaletteCtx } from "../types";

export function SearchPage({ ctx }: { ctx: PaletteCtx }) {
  const [input, setInput] = useState("");
  const { groups, loading } = useResolver(input, ctx);
  const empty = groups.length === 0;

  return (
    <>
      <div className="flex h-16 shrink-0 items-center gap-3 border-b border-border px-5">
        <MagnifyingGlass className="size-5 shrink-0 text-muted-foreground/70" />
        <CommandPrimitive.Input
          autoFocus
          value={input}
          onValueChange={setInput}
          data-palette-input="true"
          placeholder="Search the app, run a command, or jump anywhere…"
          className="h-full flex-1 bg-transparent text-[15px] outline-none placeholder:text-muted-foreground/60"
        />
        {loading && (
          <CircleNotch className="size-4 animate-spin text-muted-foreground/70" />
        )}
      </div>

      <CommandList
        className={[
          "max-h-none flex-1 overflow-y-auto p-1.5",
          // Group heading uses the standard shadcn slot — small uppercase
          // tracking-wide muted label tucked to the top-left of the group.
          "[&_[cmdk-group-heading]]:px-3",
          "[&_[cmdk-group-heading]]:pb-1",
          "[&_[cmdk-group-heading]]:pt-3",
          "[&_[cmdk-group-heading]]:text-caption",
          "[&_[cmdk-group-heading]]:font-semibold",
          "[&_[cmdk-group-heading]]:uppercase",
          "[&_[cmdk-group-heading]]:tracking-wider",
          "[&_[cmdk-group-heading]]:text-muted-foreground/70",
        ].join(" ")}
      >
        <CommandEmpty className="py-16 text-center text-sm text-muted-foreground">
          {input.trim().length === 0 ? "Type to search…" : "No results."}
        </CommandEmpty>

        {!empty &&
          groups.map((g, i) => (
            <Fragment key={g.feature}>
              {i > 0 && <CommandSeparator className="my-1 bg-border/50" />}
              <CommandGroup heading={g.label}>
                {g.results.map((r) => (
                  <ResultRow key={r.id} result={r} ctx={ctx} />
                ))}
              </CommandGroup>
            </Fragment>
          ))}
      </CommandList>

      <PaletteFooter />
    </>
  );
}
